import React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import { Translate } from 'react-localize-redux';

import moment from 'moment';

import styled, { css } from 'styled-components';

import { merchReleaseInitiatedEvent } from '@webapp/utils/rudderstack/events';

// Store
import AppState from '@webapp/store/state';
import { showMainLoader } from '@webapp/store/ui/selectors';

// Types
import SimpleArtist from '@common/types/artist/simple-artist';
import MerchFilter from '@common/types/merch/merch-filter-type';
import CatalogFilter from '@common/types/catalog/catalog-filter-type';

// Services
import { MerchProduct } from '@services/merch/types/api/merch-product';

// Components
import Grid from '@components/basics/grid';
import MerchRow from '@components/merch-row';
import Avatar from '@components/basics/avatar';
import Typography from '@components/basics/typography';

import EmptySectionBox from '../empty-wrapper';

import NoResults from '../no-results';

declare type CatalogMerchProps = {
  artistId?: string;
  artistName?: string;
  products: MerchProduct[];
  activeArtist: SimpleArtist;
  isMobile: boolean;
  loading: boolean;
  push: (path: string) => CallHistoryMethodAction;
  status?: string | null;
  filterOption: CatalogFilter;
};

const mapStateToProps = (state: AppState) => ({
  artistId: state.artist.active?.id,
  artistName: state.artist.active?.name,
  isMobile: state.ui.mobile,
  loading: showMainLoader(state),
  activeArtist: state.artist.active as SimpleArtist,
  releaseData: state.catalog.releases.items,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  push: (path: string) => dispatch(push(path)),
});

const HEADER_TITLES = [
  { id: 'merch', label: <Translate id="CATALOG.HEADER.RELEASE" /> },
  { id: 'date-added', label: <Translate id="CATALOG.HEADER.DATE_ADDED" /> },
  { id: 'price-profit', label: <Translate id="CATALOG.HEADER.PRICE_PROFIT" /> },
  { id: 'status', label: <Translate id="CATALOG.HEADER.STATUS" /> },
];

const filterRelease = (
  i: MerchProduct,
  filterOption: MerchFilter | undefined
) => {
  let filter = true;
  if (filterOption) {
    if (filterOption.searchText)
      filter =
        filter &&
        i.title
          .toLocaleLowerCase()
          .includes(filterOption.searchText.toLocaleLowerCase());
    if (filterOption?.dateAdded)
      filter =
        filter &&
        moment(new Date(i.createdAt)).isBetween(
          new Date(filterOption.dateAdded.startDate),
          new Date(filterOption.dateAdded.endDate)
        );
    if (filterOption.status) {
      if (filterOption.status.length === 0) return filter;

      filter = filter && filterOption.status.includes(i.status);
    }
  }
  return filter;
};

// TODO: DEVTEAM-603 - This needs to be integrated.
const isMultipleArtist = false;

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(function CatalogMerch({
  products,
  activeArtist,
  isMobile,
  loading,
  push,
  artistId,
  artistName,
  filterOption,
}: CatalogMerchProps) {
  // TODO: DEVTEAM-603 - This needs to be integrated with all the artists that have products.
  const artists: SimpleArtist[] = [activeArtist];

  const createMerchProduct = () => {
    merchReleaseInitiatedEvent({
      artist_id: artistId,
      artist_name: artistName,
    }).then(() => {
      push('/merch');
    });
  };

  if ((products.length === 0 || !products) && !loading) {
    return (
      <>
        <EmptySectionBox
          header={<Translate id="CATALOG.MERCH.EMPTY.TITLE" />}
          title={<Translate id="CATALOG.MERCH.EMPTY.ADD.TITLE" />}
          text={<Translate id="CATALOG.MERCH.EMPTY.ADD.TEXT" />}
          onClick={createMerchProduct}
        />
      </>
    );
  }

  return (
    <div>
      {!isMobile ? (
        <TableTitles>
          {HEADER_TITLES.map((item) => (
            <div className={item.id} key={item.id}>
              <Typography weight="700">{item.label}</Typography>
            </div>
          ))}
        </TableTitles>
      ) : null}
      {artists.map((artist, i) => (
        <CatalogTabWrapper key={i} isMobile={isMobile}>
          {isMultipleArtist && (
            <FixedAvatar src={artist.avatar} title={artist.name} />
          )}
          {!!products.filter((i) => filterRelease(i, filterOption)).length ? (
            products
              .filter((i) => filterRelease(i, filterOption))
              .map((item, i) => (
                <MerchRow
                  item={item}
                  artist={activeArtist}
                  key={`#${item.id}#${i}`}
                  isFilteredByAddedDate={!!filterOption?.dateAdded}
                />
              ))
          ) : (
            <NoResults />
          )}
        </CatalogTabWrapper>
      ))}
    </div>
  );
});

const TableTitles = styled(Grid)`
  grid-template-columns: repeat(18, minmax(0, 1fr));
  grid-gap: 30px;
  align-items: center;
  padding: 0 30px 30px;

  .merch {
    grid-column: 1 / 7;
  }
  .date-added {
    grid-column: 7 / 9;
  }
  .price-profit {
    grid-column: 9 / 11;
  }
  .status {
    grid-column: 11 / 17;
  }
  .actions {
    grid-column: 17 / 19;
  }

  ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
    .merch {
      grid-column: 1 / 9;
    }
    .date-added {
      grid-column: 9 / 11;
    }
    .price-profit {
      grid-column: 11 / 13;
    }
    .status {
      grid-column: 13 / 17;
    }
    .actions {
      grid-column: 17 / 19;
    }
  }
`;

const CatalogTabWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-gap: 10px;

  ${(props) =>
    !props.isMobile
      ? css`
          position: relative;
        `
      : ''}
`;

const FixedAvatar = styled(Avatar)`
  margin: 20px 16px 20px 0;
  display: inline-block;

  &:last-child {
    margin-right: 0;
  }

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    margin: 0;
    position: absolute;
    top: 0;
    left: calc(-10px - 32px);
  }

  ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
    left: calc(-40px - 32px);
  }
`;
